import { useState, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import systemConfigurationService, { SystemConfiguration, WhatsAppSettingsConfig, ContabilidadSettingsConfig, PresupuestosSettingsConfig } from '@/services/systemConfigurationService';
import type { ConfigurationCategory } from '../types';

// Simple toast replacement for now
const toast = {
  success: (message: string) => console.log('Success:', message),
  error: (message: string) => console.error('Error:', message),
  info: (message: string) => console.info('Info:', message)
};

export const useSystemConfiguration = () => {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<ConfigurationCategory>('subtramites');
  const [hasChanges, setHasChanges] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Partial<SystemConfiguration>>({});
  // Query to fetch the configuration
  const {
    data: configuration,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['system-configuration'],
    queryFn: systemConfigurationService.getAll,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2
  });

  // Calculate current configuration with pending changes applied
  const currentConfiguration = useMemo(() => {
    if (!configuration) return undefined;

    return {
      ...configuration,
      ...pendingChanges
    };
  }, [configuration, pendingChanges]);

  // Mutation to update the configuration
  const updateConfigurationMutation = useMutation({
    mutationFn: systemConfigurationService.updateAll,
    onSuccess: (updatedConfig) => {
      // Update the cache with the new configuration
      queryClient.setQueryData(['system-configuration'], updatedConfig);

      // Reset pending changes
      setPendingChanges({});
      setHasChanges(false);

      toast.success('Configuración actualizada correctamente');
    },
    onError: (error: any) => {
      console.error('Error updating configuration:', error);
      toast.error('Error al actualizar la configuración: ' + (error?.message || 'Error desconocido'));
    }
  });

  // Function to update a section of the configuration
  const updateSection = (section: keyof SystemConfiguration, data: any) => {
    const newChanges = {
      ...pendingChanges,
      [section]: data
    };

    setPendingChanges(newChanges);
    setHasChanges(true);
  };

  // Function to save all pending changes
  const saveChanges = async () => {
    if (!currentConfiguration || !hasChanges) return;

    updateConfigurationMutation.mutate(currentConfiguration);
  };

  // Function to discard pending changes
  const discardChanges = () => {
    setPendingChanges({});
    setHasChanges(false);
    toast.info('Cambios descartados');
  };


  // Function to update subtramites configuration specifically
  const updateSubtramitesConfiguration = (subtramites: string[]) => {
    updateSection('subtramites_configuration', {
      default_enabled: subtramites
    });
  };

  const updateWhatsAppConfiguration = (whatsappSettings: WhatsAppSettingsConfig) => {
    updateSection('whatsapp_settings', whatsappSettings);
  };

  const updateContabilidadConfiguration = (contabilidadSettings: ContabilidadSettingsConfig) => {
    updateSection('contabilidad_settings', contabilidadSettings);
  };

  const updatePresupuestosConfiguration = (presupuestosSettings: PresupuestosSettingsConfig) => {
    updateSection('presupuestos_settings', presupuestosSettings);
  };

  // Function to reset configuration to defaults
  const resetToDefaults = () => {
    if (!configuration) return;

    const defaultConfig = systemConfigurationService.getDefaultConfiguration();
    // Calculate the difference between current config and default config
    const defaultPendingChanges: Partial<SystemConfiguration> = {};

    // Compare each section and only include changes
    Object.keys(defaultConfig).forEach(key => {
      const configKey = key as keyof SystemConfiguration;
      if (JSON.stringify(configuration[configKey]) !== JSON.stringify(defaultConfig[configKey])) {
        (defaultPendingChanges as any)[configKey] = defaultConfig[configKey];
      }
    });

    setPendingChanges(defaultPendingChanges);
    setHasChanges(Object.keys(defaultPendingChanges).length > 0);
    toast.info('Configuración restablecida a valores por defecto');
  };

  return {
    // Data
    configuration: currentConfiguration,
    originalConfiguration: configuration,
    isLoading,
    isError,
    error,

    // State
    activeTab,
    setActiveTab,
    hasChanges,
    pendingChanges,

    // Actions
    updateSection,
    updateSubtramitesConfiguration,
    updateWhatsAppConfiguration,
    updateContabilidadConfiguration,
    updatePresupuestosConfiguration,
    saveChanges,
    discardChanges,
    resetToDefaults,

    // Status
    isSaving: updateConfigurationMutation.isPending,
    saveError: updateConfigurationMutation.error
  };
};
